import { Alert, Box, Button, Skeleton, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import type { HouseholdModelPortfolioDraftError } from './useHouseholdModelPortfolioDraft';

export const DRAFT_ERROR_COPY: Record<
  HouseholdModelPortfolioDraftError,
  string
> = {
  MISSING_HOUSEHOLD: 'householdModelPortfolioIps.missingHousehold',
  MISSING_MODEL: 'householdModelPortfolioIps.missingModel',
  LOAD_FAILED: 'householdModelPortfolioIps.loadError',
};

/**
 * Stands in while the household's model is loaded. Steps keep their form out of
 * the tree until then, since their fields seed their state from the draft once.
 */
export const HouseholdModelPortfolioDraftSkeleton = ({
  title,
  testId,
}: {
  title: string;
  testId: string;
}) => (
  <Box data-testid={`${testId}-skeleton`}>
    <Typography variant='displayLarge' sx={{ mt: 1 }}>
      {title}
    </Typography>
    {[0, 1, 2].map((row) => (
      <Skeleton key={row} height={56} sx={{ mb: 2 }} />
    ))}
  </Box>
);

/**
 * Shown when there is no model to work from. The step is deliberately withheld
 * rather than rendered empty, since anything the advisor does has nothing to
 * attach to.
 *
 * A load that failed is worth another try in place; a household with no model
 * attached is not, and those callers leave `onRetry` off.
 */
export const HouseholdModelPortfolioDraftUnavailable = ({
  title,
  message,
  testId,
  onRetry,
}: {
  title: string;
  message: string;
  testId: string;
  onRetry?: () => void;
}) => {
  const { t } = useTranslation('shared');

  return (
    <Box data-testid={`${testId}-unavailable`}>
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {title}
      </Typography>
      <Alert
        severity='warning'
        sx={{ mt: 2 }}
        data-testid={`${testId}-unavailable-message`}
      >
        {message}
      </Alert>
      {onRetry && (
        <Button
          variant='contained'
          sx={{ mt: 2 }}
          data-testid={`${testId}-retry-button`}
          onClick={onRetry}
          color='secondary'
        >
          {t('retry')}
        </Button>
      )}
    </Box>
  );
};
